import { CallState } from "@b2bportal/core-types";
import { AccountSelectors } from "@/modules/account";
import type { RootState } from "@/store";

/**
 * RBC-specific flight tracking properties
 */
export interface RbcFlightTrackingProperties {
  /** Whether the searched route is eligible for fixed points grid pricing */
  is_grid_eligible?: boolean;
  /** Reason why grid is not eligible: Not_elite, route_not_eligible, points_balance_not_met */
  grid_ineligible?: string;
  /** Current pricing tab (fixed or flex) */
  rbc_price_tab?: "fixed" | "flex";
  /** Whether user needs to purchase additional points to meet fixed points cost */
  additional_points_purchase?: boolean;
  /** Points to purchase (rounded up to nearest 1,000-point block) */
  max_point_overage?: number;
  /** Exact points gap between flight cost and user's current balance */
  additional_points_needed?: number;
  /** Multiplier for converting points to cash in flex pricing (e.g. 0.015 = 100 pts → $1.50) */
  flex_conversion_rate?: number;
}

/**
 * Extracts RBC-specific tracking properties from Redux state
 *
 * @param state - Root Redux state
 * @returns RBC tracking properties object
 */
export const getRbcFlightTrackingProperties = (
  state: RootState
): RbcFlightTrackingProperties => {
  const flightShop = state.flights?.flightShop;

  if (!flightShop) {
    return {};
  }

  const pricingMode = flightShop.pricingMode;
  const additionalPointsPurchase = flightShop.additionalPointsPurchase;
  const flexConversionRate =
    AccountSelectors.getBasePointsToCashMultiplier(state);

  // Grid is eligible if fixed pricing call didn't fail
  const isGridEligible =
    flightShop.fixedPricingFlightsCallState !== CallState.Failed;

  // Determine grid ineligibility reason
  let gridIneligible: string | undefined;
  if (!isGridEligible) {
    if (flightShop.shopSummaryDestinationIneligibleForGridPricing) {
      gridIneligible = "route_not_eligible";
    } else if (flightShop.shopSummaryInsufficientPointsBalance) {
      gridIneligible = "points_balance_not_met";
    } else if (flightShop.routeNotAllowedForFixedPointsPricing) {
      gridIneligible = "route_not_eligible";
    }
    // Note: "Not_elite" would need to come from user account data
  }

  // pointsGap: exact points deficit (e.g., 4,300)
  const exactPointsGap = additionalPointsPurchase?.pointsGap;

  // purchaseAmount: points rounded to nearest 1,000-point block (e.g., 5,000)
  const purchaseAmountRounded = additionalPointsPurchase?.purchaseAmount;

  const rbcProperties = {
    is_grid_eligible: isGridEligible,
    grid_ineligible: gridIneligible,
    rbc_price_tab: pricingMode,
    additional_points_purchase: !!additionalPointsPurchase,
    max_point_overage: purchaseAmountRounded,
    additional_points_needed: exactPointsGap,
    flex_conversion_rate: flexConversionRate,
  };

  return rbcProperties;
};
